<template>
  <app-view title="我的">
    <main class="min-h-app bg-gray-50 pb-24">
      <section class="bg-white px-4 py-4">
        <div
          v-if="$auth.loggedIn"
          class="relative aspect-video overflow-hidden rounded-lg bg-primary text-white"
        >
          <img
            class="absolute inset-0 h-full w-full object-cover"
            src="/imgs/member_card.png"
            alt=""
          />
          <div class="absolute inset-0 bg-black/10"></div>
          <div class="absolute inset-x-0 bottom-0 z-10 px-5 py-4">
            <div class="min-w-0 pr-24 font-semibold drop-shadow">
              <p class="mb-2 break-all text-sm">編號：{{ _my.no }}</p>
              <p class="mb-2 truncate text-sm">會員：{{ _my.name }}</p>
              <p class="text-sm">電話：({{ _my.code }}){{ _my.tel }}</p>
            </div>
            <QRcode
              :text="_my.hashid"
              img="/logo.png"
              class="absolute right-5 bottom-4 h-16 w-16 rounded bg-white p-1 pc:h-20 pc:w-20"
              :version="2"
            />
          </div>
        </div>

        <div v-else class="flex items-center justify-between gap-4">
          <div class="min-w-0">
            <p class="text-xs text-gray-400">會員帳戶</p>
            <h1 class="mt-1 text-lg font-bold text-gray-900">尚未登入</h1>
            <p class="mt-1 text-xs text-gray-500">登入後可查看訂單、管理遊戲 ID 與帳號綁定。</p>
          </div>
          <nuxt-link :to="go('/auth/login')" class="btn flex-none px-5 text-sm">
            登入
          </nuxt-link>
        </div>
      </section>

      <section class="mt-2 grid grid-cols-2 divide-x divide-gray-100 border-y border-gray-100 bg-white">
        <nuxt-link
          v-for="action in primaryActions"
          :key="action.text"
          :to="action.to"
          class="flex items-center gap-3 px-4 py-4 active:bg-gray-50"
        >
          <component :is="action.icon" class="h-5 w-5 flex-none text-primary" />
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold text-gray-900">{{ action.text }}</p>
            <p class="mt-0.5 truncate text-[11px] text-gray-400">{{ action.caption }}</p>
          </div>
        </nuxt-link>
      </section>

      <section
        v-for="section in visibleSections"
        :key="section.title"
        class="mt-2 border-y border-gray-100 bg-white"
      >
        <h2 class="px-4 pt-4 pb-2 text-xs font-semibold text-gray-400">{{ section.title }}</h2>
        <div class="divide-y divide-gray-100">
          <component
            :is="item.type === 'button' ? 'button' : 'nuxt-link'"
            v-for="item in section.items"
            :key="item.text"
            :to="item.to"
            type="button"
            class="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50"
            @click="item.click && item.click()"
          >
            <component :is="item.icon" class="h-5 w-5 flex-none text-gray-400" />
            <div class="min-w-0 flex-grow">
              <p
                class="truncate text-sm font-medium"
                :class="item.danger ? 'text-rose-600' : 'text-gray-800'"
              >
                {{ item.text }}
              </p>
              <p v-if="item.caption" class="mt-0.5 truncate text-[11px] text-gray-400">
                {{ item.caption }}
              </p>
            </div>
            <ChevronRightIcon v-if="item.type !== 'button'" class="h-4 w-4 flex-none text-gray-300" />
          </component>
        </div>
      </section>
    </main>
  </app-view>
</template>

<script>
import { mapActions } from "vuex";
import {
  ChevronRightIcon,
  ClipboardListIcon,
  CogIcon,
  DocumentTextIcon,
  IdentificationIcon,
  InformationCircleIcon,
  LinkIcon,
  LoginIcon,
  LogoutIcon,
  QuestionMarkCircleIcon,
  RefreshIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
} from "@vue-hero-icons/outline";
import QRcode from "@/components/QRcode.vue";

export default {
  auth: false,
  components: {
    ChevronRightIcon,
    ClipboardListIcon,
    CogIcon,
    DocumentTextIcon,
    IdentificationIcon,
    InformationCircleIcon,
    LinkIcon,
    LoginIcon,
    LogoutIcon,
    QuestionMarkCircleIcon,
    RefreshIcon,
    ShieldCheckIcon,
    ShoppingBagIcon,
    QRcode,
  },
  head() {
    return {
      title: `${this.appName}-我的`,
    };
  },
  computed: {
    appName() {
      return this.$store.getters["app/info"]?.name || process.env.APP_NAME;
    },
    primaryActions() {
      return [
        {
          text: "我的訂單",
          caption: "查看購買紀錄",
          to: this.go("/takings"),
          icon: "ClipboardListIcon",
        },
        {
          text: "前往購物",
          caption: "瀏覽商品",
          to: this.go("/order"),
          icon: "ShoppingBagIcon",
        },
      ];
    },
    visibleSections() {
      return this.sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => this.showable(item)),
        }))
        .filter((section) => section.items.length);
    },
    sections() {
      return [
        {
          title: "帳戶",
          items: [
            {
              text: "會員登入",
              caption: "登入或註冊會員",
              to: this.go("/auth/login"),
              icon: "LoginIcon",
              guest: true,
            },
            {
              text: "帳號設置",
              caption: "更新會員資料",
              to: this.go("/setting"),
              icon: "CogIcon",
              auth: true,
            },
            {
              text: "我的遊戲ID",
              caption: "管理常用遊戲帳號",
              to: this.go("/game-ids"),
              icon: "IdentificationIcon",
              auth: true,
            },
            {
              text: "賬號綁定",
              caption: "管理第三方登入",
              to: this.go("/bindAccount"),
              icon: "LinkIcon",
              auth: true,
            },
          ],
        },
        {
          title: "幫助",
          items: [
            {
              text: "購買流程",
              to: this.go("/procedure"),
              icon: "QuestionMarkCircleIcon",
            },
            {
              text: "退換政策",
              to: this.go("/teams/policies/refund"),
              icon: "RefreshIcon",
            },
            {
              text: "關於我們",
              to: this.go("/about"),
              icon: "InformationCircleIcon",
            },
          ],
        },
        {
          title: "條款",
          items: [
            {
              text: "用戶註冊協議",
              to: this.go("/teams/policies/service"),
              icon: "DocumentTextIcon",
            },
            {
              text: "私隱政策",
              to: this.go("/teams/policies/privacy"),
              icon: "ShieldCheckIcon",
            },
          ],
        },
        {
          title: "登入狀態",
          items: [
            {
              text: "登出",
              caption: "退出目前會員帳戶",
              click: () => this.logout(),
              type: "button",
              icon: "LogoutIcon",
              danger: true,
              auth: true,
            },
          ],
        },
      ];
    },
  },
  methods: {
    ...mapActions({
      empty: "cart/empty",
    }),
    go(line) {
      return this.localePath(line);
    },
    showable(item) {
      if (item.guest && this.$auth.loggedIn) return false;
      if (item.auth && !this.$auth.loggedIn) return false;

      return true;
    },
    logout() {
      if (!confirm("確定要登出會員？")) {
        return;
      }

      this.$auth.logout().then(() => {
        this.empty();
        this.$router.push(this.localePath("/auth/login"));
      });
    },
  },
};
</script>
